import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { request } from "@/shared/http/client";

/** Um resumo de versão — nunca os documentos (eles pesam centenas de KB). */
export interface SiteSummary {
	readonly id: string;
	readonly name: string;
	readonly hasDraft: boolean;
	readonly isPublished: boolean;
	readonly publishedAt: string | null;
	readonly draftBehindPublish: boolean;
	readonly subdomain: string | null;
	readonly customDomain: string | null;
	readonly customDomainVerifiedAt: string | null;
	readonly isDefault: boolean;
	readonly previewKey: string;
	readonly theme: Record<string, unknown>;
	readonly tracking: { metaPixelId?: string | null; ga4Id?: string | null };
	/**
	 * Que chat esta versão embute (0265) — escolha do Ben, só leitura aqui.
	 * Opcional porque um servidor de antes de 0265 não o manda.
	 */
	readonly chat?: SiteChat;
	readonly updatedAt: string | null;
}

/** Ver `domain/site-chat.ts` no back: `auto` = o primeiro webchat público. */
export interface SiteChat {
	readonly choice: "auto" | "channel" | "none";
	readonly channelId: string | null;
	readonly channelName: string | null;
	/** Canal escolhido apagado ou fora do ar: a página sai sem balão. */
	readonly unavailable: boolean;
}

export interface RevisionSummary {
	readonly id: string;
	readonly source: "builder" | "import" | "restore" | "manual";
	readonly sizeBytes: number;
	readonly createdAt: string;
}

export interface DnsInstruction {
	readonly type: "CNAME" | "A";
	readonly host: string;
	readonly value: string;
}

export interface AddressView {
	readonly subdomain: string | null;
	readonly platformHost: string | null;
	readonly customDomain: string | null;
	readonly verifiedAt: string | null;
	readonly baseDomain: string;
	readonly instructions: DnsInstruction[];
	/** O servidor não conseguiu montar os registros de DNS desta vez — não é "sem registros". */
	readonly instructionsUnavailable?: boolean;
}

const KEY = ["site", "versions"] as const;

export function useSites() {
	return useQuery({
		queryKey: KEY,
		queryFn: () => request<{ sites: SiteSummary[] }>("/v3/site").then((r) => r.sites),
	});
}

function invalidating<TInput, TOut>(fn: (input: TInput) => Promise<TOut>) {
	const queries = useQueryClient();
	return useMutation({
		mutationFn: fn,
		onSuccess: () => void queries.invalidateQueries({ queryKey: ["site"] }),
	});
}

export function useCreateSite() {
	return invalidating((input: { name: string }) =>
		request<SiteSummary>("/v3/site", { method: "POST", body: input }),
	);
}

export function useUpdateSite() {
	return invalidating(
		(input: {
			id: string;
			name?: string;
			theme?: Record<string, unknown>;
			tracking?: { metaPixelId?: string | null; ga4Id?: string | null };
			makeDefault?: boolean;
		}) =>
			request<SiteSummary>(`/v3/site/${input.id}`, {
				method: "PATCH",
				body: {
					name: input.name,
					theme: input.theme,
					tracking: input.tracking,
					makeDefault: input.makeDefault,
				},
			}),
	);
}

export function useDeleteSite() {
	return invalidating((input: { id: string }) =>
		request<{ deleted: boolean }>(`/v3/site/${input.id}`, { method: "DELETE" }),
	);
}

export function usePublishSite() {
	return invalidating((input: { id: string }) =>
		request<SiteSummary>(`/v3/site/${input.id}/publish`, { method: "POST" }),
	);
}

export function useUnpublishSite() {
	return invalidating((input: { id: string }) =>
		request<SiteSummary>(`/v3/site/${input.id}/unpublish`, { method: "POST" }),
	);
}

export function useRevisions(siteId: () => string | null) {
	return useQuery({
		queryKey: ["site", "revisions", siteId] as const,
		queryFn: () =>
			request<{ revisions: RevisionSummary[] }>(`/v3/site/${siteId()}/revisions`).then(
				(r) => r.revisions,
			),
		enabled: () => siteId() !== null,
	});
}

export function useRestoreRevision() {
	return invalidating((input: { siteId: string; revisionId: string }) =>
		request<{ restored: boolean }>(
			`/v3/site/${input.siteId}/revisions/${input.revisionId}/restore`,
			{ method: "POST" },
		),
	);
}

export function useSiteAddress(siteId: () => string | null) {
	return useQuery({
		queryKey: ["site", "address", siteId] as const,
		queryFn: () => request<AddressView>(`/v3/site/${siteId()}/address`),
		enabled: () => siteId() !== null,
	});
}

export function useSetAddress() {
	return invalidating(
		(input: { siteId: string; subdomain?: string | null; customDomain?: string | null }) =>
			request<AddressView>(`/v3/site/${input.siteId}/address`, {
				method: "PUT",
				body: { subdomain: input.subdomain, customDomain: input.customDomain },
			}),
	);
}

export function useVerifyDomain() {
	return invalidating((input: { siteId: string }) =>
		request<AddressView>(`/v3/site/${input.siteId}/address/verify`, { method: "POST" }),
	);
}

export function useSiteHtml() {
	return useMutation({
		mutationFn: (input: { siteId: string }) =>
			request<{ html: string | null; publishedHtml: string | null }>(
				`/v3/site/${input.siteId}/html`,
			),
	});
}

/**
 * As duas recusas do import em modo `replace` que o servidor devolve em inglês
 * (são a mensagem do log, não uma frase para a tela). Reconhecidas aqui, em UM
 * lugar, para a tela dizer a sua própria frase — com o limite escrito.
 */
export type ImportRefusal = "notDocument" | "tooLarge";

export function importRefusalOf(e: unknown): ImportRefusal | null {
	const error = e as { message?: unknown; payload?: { message?: unknown } } | null;
	// A frase pode vir no `message` do erro ou só no corpo (`payload.message`).
	const message = [error?.message, error?.payload?.message]
		.filter((m): m is string => typeof m === "string")
		.join(" ")
		.toLowerCase();
	if (message.includes("full html document")) return "notDocument";
	if (message.includes("1.5mb") || message.includes("are refused")) return "tooLarge";
	return null;
}

export function useImportHtml() {
	return invalidating((input: { siteId: string; html: string }) =>
		request<{ bytes: number }>(`/v3/site/${input.siteId}/html`, {
			method: "PUT",
			body: { html: input.html, source: "import" },
		}),
	);
}

/** A porta pública do rascunho/publicado, montada aqui para UM lugar saber a forma. */
export function previewUrl(input: {
	tenantSlug: string;
	site: SiteSummary;
	draft: boolean;
	edit: boolean;
}): string {
	const base = `/v3/public/web/${input.tenantSlug}?site=${input.site.id}`;
	if (!input.draft) return base;
	const edit = input.edit ? "&edit=1" : "";
	return `${base}&draft=1&key=${encodeURIComponent(input.site.previewKey)}${edit}`;
}

/** Uma chamada do site a uma função (`GET /v3/run-history?origin=site`). */
export interface SiteRun {
	readonly id: string;
	readonly code: string;
	readonly origin: string;
	readonly ranAt: string;
	readonly error: string | null;
	readonly siteId: string | null;
	readonly ip: string | null;
	readonly signedIn: { readonly entity: string; readonly id: string } | null;
	readonly writes: number;
	readonly undoneAt: string | null;
}

/** USO DO SITE — os últimos sete dias de chamadas do site às funções (admin). */
export function useSiteRuns(enabled: () => boolean) {
	return useQuery({
		queryKey: ["site", "runs"] as const,
		enabled,
		queryFn: async () => {
			const since = new Date(Date.now() - 7 * 86_400_000).toISOString();
			const out = await request<{ runs: SiteRun[] }>(
				`/v3/run-history?origin=site&limit=200&since=${encodeURIComponent(since)}`,
			);
			return out.runs;
		},
	});
}

/**
 * DESFAZER o que o site gravou: uma execução (`runId`), ou todas as do período —
 * de um visitante só quando vem `ip`. O que alguém mudou depois fica, sem `force`.
 */
export function useUndoSiteRuns() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { runId?: string; ip?: string; force?: boolean }) =>
			input.runId
				? request<{ undone: number; skipped: unknown[] }>(
						`/v3/run-history/${encodeURIComponent(input.runId)}/undo`,
						{ method: "POST", body: input.force ? { force: true } : {} },
					).then((r) => ({ undone: r.undone, skipped: r.skipped.length }))
				: request<{ undone: number; skipped: number }>("/v3/run-history/undo", {
						method: "POST",
						body: {
							origin: "site",
							since: new Date(Date.now() - 7 * 86_400_000).toISOString(),
							...(input.ip ? { ip: input.ip } : {}),
							...(input.force ? { force: true } : {}),
						},
					}),
		onSuccess: () => client.invalidateQueries(),
	});
}
